import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { analytics, EVENTS, EVENT_FN } from "../../analytics";
import { handleCheckout } from "../../utils/checkout";
import { useExchangeRate, toKRWLabel } from "../../hooks/useExchangeRate";
import { SERVICE_PRICES, diagnosisPrice } from "../../constant";
import ServiceUnavailableModal from "../../components/ServiceUnavailableModal";
import InterestCaptureModal from "../../components/InterestCaptureModal";
import { isServicePaused } from "../../config/pausedServices";

interface ServiceEntry {
  id: string;
  badge: string;
  name: string;
  anchor: string;
  heading: string;
  description: string;
  features: string[];
  forWhom: string;
  cta: string;
  highlight: boolean;
}

const ServicePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { rate } = useExchangeRate();
  const [unavailableService, setUnavailableService] = useState<ServiceEntry | null>(null);

  const s = t("serviceHubPage", { returnObjects: true }) as {
    hero: { title: string; subtitle: string };
    services: ServiceEntry[];
    notSure: { heading: string; body: string; cta: string };
  };

  const withPrice = (text: string) => text.replace("{Check_Price}", diagnosisPrice.toString());


  // Track which service section the user actually reads (fires once per section).
  // Uses a 1.5s dwell timer: section must stay in view for 1.5s before firing.
  // Scrolling past quickly (e.g. anchor jump to a different section) won't trigger.
  useEffect(() => {
    const fired = new Set<string>();
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const observers: IntersectionObserver[] = [];

    s.services.forEach((svc) => {
      const el = document.getElementById(svc.anchor);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !fired.has(svc.id)) {
            const t = setTimeout(() => {
              fired.add(svc.id);
              timers.delete(svc.id);
              analytics.track(EVENT_FN.viewService(svc.id));
            }, 1500);
            timers.set(svc.id, t);
          } else {
            const t = timers.get(svc.id);
            if (t) { clearTimeout(t); timers.delete(svc.id); }
          }
        },
        { threshold: 0.5 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => {
      observers.forEach((obs) => obs.disconnect());
      timers.forEach((t) => clearTimeout(t));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="font-sans bg-white text-[#0A1A3E]">
      <Helmet>
        <title>BeMe – {i18n.language === "ko" ? "서비스 소개" : "Services"}</title>
        <meta
          name="description"
          content={
            i18n.language === "ko"
              ? "영국 취업을 위한 CV 진단, 핵심 리빌딩, 시그니처 서비스 — BeMe Consulting"
              : "CV Diagnosis, Core Rebuild, and Signature services for UK job seekers — BeMe Consulting"
          }
        />
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#0A1A3E] to-[#11285E] text-white text-center px-4">
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-4xl md:text-5xl font-extrabold mb-5"
        >
          {s.hero.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-white/75 text-lg max-w-xl mx-auto whitespace-pre-line"
        >
          {s.hero.subtitle}
        </motion.p>

        {/* Quick nav */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="flex flex-wrap gap-3 justify-center mt-10"
        >
          {s.services.map((svc) => (
            <button
              key={svc.id}
              onClick={() => {
                document.getElementById(svc.anchor)?.scrollIntoView({ behavior: "smooth" });
                analytics.track(EVENT_FN.anchorClick(svc.id));
              }}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                svc.highlight
                  ? "bg-accent text-main hover:bg-yellow-400"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {svc.name} — £{SERVICE_PRICES[svc.id]}
              {rate && SERVICE_PRICES[svc.id] && (
                <span className="ml-1 opacity-70 font-normal">
                  ({toKRWLabel(SERVICE_PRICES[svc.id] * rate)})
                </span>
              )}
            </button>
          ))}
        </motion.div>
      </section>

      {/* Service sections */}
      {s.services.map((svc, i) => (
        <section
          key={svc.id}
          id={svc.anchor}
          className={`py-20 px-4 ${svc.highlight ? "bg-[#0A1A3E] text-white" : i % 2 === 0 ? "bg-white" : "bg-[#F7F8FB]"}`}
        >
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge + price */}
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`text-xs font-bold px-3 py-1 rounded-full ${
                    svc.highlight ? "bg-accent text-main" : "bg-main text-white"
                  }`}
                >
                  {svc.badge}
                </span>
                <div className="flex flex-col leading-tight">
                  <span className={`text-3xl font-extrabold ${svc.highlight ? "text-accent" : "text-main"}`}>
                    £{SERVICE_PRICES[svc.id]}
                  </span>
                  {rate && SERVICE_PRICES[svc.id] && (
                    <span className={`text-sm font-medium mt-0.5 ${svc.highlight ? "text-white/60" : "text-gray-400"}`}>
                      {toKRWLabel(SERVICE_PRICES[svc.id] * rate)}
                    </span>
                  )}
                </div>
              </div>

              {/* Service name */}
              <h2
                className={`text-2xl md:text-3xl font-extrabold mb-3 ${
                  svc.highlight ? "text-white" : "text-main"
                }`}
              >
                {svc.name}
              </h2>

              {/* Heading */}
              <p
                className={`text-lg font-semibold mb-5 ${
                  svc.highlight ? "text-white/80" : "text-gray-700"
                }`}
              >
                {svc.heading}
              </p>

              {/* Description */}
              <p
                className={`text-sm leading-relaxed mb-8 ${
                  svc.highlight ? "text-white/70" : "text-gray-500"
                }`}
              >
                {svc.description}
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {svc.features.map((f, fi) => (
                  <li
                    key={fi}
                    className={`flex items-start gap-3 text-sm ${
                      svc.highlight ? "text-white/90" : "text-gray-700"
                    }`}
                  >
                    <Check
                      size={16}
                      className={`mt-0.5 flex-shrink-0 ${svc.highlight ? "text-accent" : "text-main"}`}
                    />
                    {f}
                  </li>
                ))}
              </ul>

              {/* For whom */}
              <p
                className={`text-xs italic mb-8 ${
                  svc.highlight ? "text-white/50" : "text-gray-400"
                }`}
              >
                {svc.forWhom}
              </p>

              {/* CTA */}
              <button
                onClick={() => {
                  if (isServicePaused(svc.id)) {
                    analytics.track(EVENT_FN.serviceUnavailable(svc.id), { from: "servicePage" });
                    setUnavailableService(svc);
                    return;
                  }
                  analytics.track(EVENT_FN.paymentLink(svc.id), { from: "servicePage" });
                  handleCheckout(svc.id);
                }}
                className={`font-bold px-10 py-4 rounded-xl transition-colors ${
                  svc.highlight
                    ? "bg-accent text-main hover:bg-yellow-400"
                    : "bg-main text-white hover:opacity-90"
                }`}
              >
                {svc.cta}
              </button>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Not sure section */}
      <section className="py-20 bg-[#F7F8FB] px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto"
        >
          <h3 className="text-xl font-bold text-main mb-4">{s.notSure.heading}</h3>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">{withPrice(s.notSure.body)}</p>
          <button
            onClick={() => {
              navigate("/check-service");
              window.scrollTo({ top: 0, behavior: "smooth" });
              analytics.track(EVENTS.CLICK_SERVICE_NOT_SURE);
            }}
            className="bg-main text-white font-bold px-10 py-4 rounded-xl hover:opacity-90 transition-colors"
          >
            {withPrice(s.notSure.cta)}
          </button>
        </motion.div>
      </section>

      <ServiceUnavailableModal
        service={unavailableService}
        onClose={() => setUnavailableService(null)}
      />

      <InterestCaptureModal sourcePage="service" />
    </div>
  );
};

export default ServicePage;
